// «Configuración del Pricer» (F4 · T3): el formato del número de las cotizaciones (CITE) y los textos por
// defecto. Es del administrador de la empresa; el resto puede verla pero no cambiarla.
//
// El servidor es quien decide quién puede guardar (`puedeEditar`); esta pantalla sólo lo refleja. El ejemplo
// en vivo usa la MISMA función que usa el servidor al asignar el número (`cite-formato.generated.ts`), así que
// lo que se ve acá es lo que se asigna.
import { useForzarModoClaro } from '@/lib/hooks/use-forzar-modo-claro';
import { limpiarWeb } from '../lib/limpiar-web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeMiEmpresa } from '@/features/mi-empresa/components/encabezado-de-mi-empresa';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { PricerDatecStyles } from '../components/styles';
import { pricerDatecService } from '../services/pricer-datec.service';
import type {
  AjustesPricer,
  DocumentoAjustes,
  RespuestaAjustes,
} from '../types/pricer-datec.types';
import { AnexoTecnicoEditor } from '../components/documento/AnexoTecnicoEditor';
import {
  Campo,
  MENSAJE_CORREO,
  MENSAJE_TELEFONO,
  correoMalo,
  telefonoMalo,
} from '../components/documento/CamposDelDocumento';
import { problemasDelAnexo } from '../lib/anexo-tecnico';
import {
  formatearCite,
  problemaDeRepeticion,
  type AnioCite,
  type FormatoCite,
  type ReinicioCite,
  type SeparadorCite,
} from '../lib/cite-formato.generated';

const caja: React.CSSProperties = {
  padding: 20,
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 16,
  boxShadow: '0 1px 2px hsl(222 47% 11% / 0.05)',
  marginBottom: 18,
};
const titulo: React.CSSProperties = { margin: '0 0 12px', fontSize: 16, fontWeight: 600 };
const ayuda: React.CSSProperties = {
  fontSize: 12,
  color: 'hsl(var(--muted-foreground))',
  marginTop: 3,
};
const enlaceBoton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'hsl(var(--primary))',
  fontSize: 12,
  textDecoration: 'underline',
  cursor: 'pointer',
};

/** El largo de las notas y los términos a partir del cual la pantalla avisa (no bloquea). */
const LIMITE_SUAVE_TEXTO = 1500;

function Radio<T extends string>(p: {
  nombre: string;
  valor: T;
  actual: T;
  onChange: (v: T) => void;
  deshabilitado: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
      <input
        type="radio"
        name={p.nombre}
        checked={p.actual === p.valor}
        disabled={p.deshabilitado}
        onChange={() => p.onChange(p.valor)}
        style={{ marginTop: 3 }}
      />
      <span>{p.children}</span>
    </label>
  );
}

function Casilla(p: {
  texto: string;
  valor: boolean;
  deshabilitado: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
      <input
        type="checkbox"
        checked={p.valor}
        disabled={p.deshabilitado}
        onChange={(e) => p.onChange(e.target.checked)}
      />
      {p.texto}
    </label>
  );
}

const PREFIJO_VALIDO = /^[A-Z0-9][A-Z0-9 ._/-]{0,19}$/;

export function PricerDatecAjustesPage() {
  useForzarModoClaro();
  const navigate = useNavigate();
  const { toast } = useToast();
  const usuario = useAuthStore((s) => s.user);
  const [datos, setDatos] = useState<RespuestaAjustes | null>(null);
  const [cfg, setCfg] = useState<AjustesPricer | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorDeCarga, setErrorDeCarga] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorDeCarga(null);
    try {
      const r = await pricerDatecService.getAjustes();
      setDatos(r);
      setCfg(r.ajustes);
    } catch (e) {
      setErrorDeCarga(mensajeDeError(e, 'No se pudo cargar la configuración. Probá de nuevo.'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const puedeEditar = datos?.puedeEditar === true;
  const bloqueado = !puedeEditar;
  const cite = cfg?.cite;

  const cambiarCite = (p: Partial<FormatoCite>) =>
    setCfg((c) => (c ? { ...c, cite: { ...c.cite, ...p } } : c));
  const cambiarTextos = (p: Partial<AjustesPricer['textos']>) =>
    setCfg((c) => (c ? { ...c, textos: { ...c.textos, ...p } } : c));

  // F4b: el documento al cliente (pie, firma de ejemplo, textos y biblioteca de bloques). Quién puede guardar lo
  // decide el servidor (`puedeEditar`, clave `configure_pricer`).
  const doc = cfg?.documento;
  const cambiarDoc = (p: Partial<DocumentoAjustes>) =>
    setCfg((c) => (c && c.documento ? { ...c, documento: { ...c.documento, ...p } } : c));

  const hayCambios = !!datos && !!cfg && JSON.stringify(cfg) !== JSON.stringify(datos.ajustes);
  const problemaRepeticion = cite ? problemaDeRepeticion(cite) : null;
  const prefijoMalo = !!cite && !PREFIJO_VALIDO.test(cite.prefijo);
  const textosVacios =
    !!cfg && (cfg.textos.formaPago.trim() === '' || cfg.textos.plazoEntrega.trim() === '');
  const webConProtocolo = !!doc && /https?:|\/\//i.test(doc.pie.web);
  const problemasDeDocumento = doc
    ? [
        ...(webConProtocolo ? ['Escribí la web sin http://'] : []),
        ...(telefonoMalo(doc.pie.telefono) ? [MENSAJE_TELEFONO] : []),
        ...(correoMalo(doc.firma.correoComercial) ? [MENSAJE_CORREO] : []),
        ...problemasDelAnexo({ titulo: '', subtitulo: '', bloques: doc.biblioteca }),
        ...problemasDelAnexo(doc.anexoEjemplo),
      ]
    : [];
  const hayError =
    !!problemaRepeticion || prefijoMalo || textosVacios || problemasDeDocumento.length > 0;

  // El próximo número que recibiría una cotización que se envíe ahora, con el formato que se está editando.
  const proximo = useMemo(() => {
    if (!datos || !cite) return null;
    const numero = Math.max(cite.primerNumero, datos.proximoCite.ultimoUsado + 1);
    const dia = datos.proximoCite.dia;
    const quien = usuario ? `${usuario.firstName ?? ''} ${usuario.lastName ?? ''}`.trim() : '';
    return {
      numero,
      ejemplo: formatearCite(cite, {
        fecha: dia,
        lob: 'CSS',
        ejecutivo: quien || 'Ana Pérez',
        correlativo: numero,
      }),
    };
  }, [datos, cite, usuario]);

  const guardar = async () => {
    if (!cfg || hayError) return;
    setGuardando(true);
    try {
      const r = await pricerDatecService.guardarAjustes(cfg);
      setDatos(r);
      setCfg(r.ajustes);
      toast({
        title: 'Configuración guardada',
        description: 'Rige para las cotizaciones que se envíen desde ahora.',
      });
    } catch (e) {
      toast({
        title: 'No se pudo guardar',
        description: mensajeDeError(e, 'Probá de nuevo en unos minutos.'),
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const restaurar = () => datos && setCfg(datos.porDefecto);

  return (
    <AppLayout>
      <div className="pd-root" style={{ color: 'hsl(var(--foreground))', minHeight: 600 }}>
        <PricerDatecStyles />
        {/*
          Mi empresa › Documentos (rediseño del 18-oct, artefacto): cómo se ve lo
          que la empresa envía. El encabezado es el de Mi empresa; el cuerpo, el
          de siempre del Pricer (en claro, como todo el Pricer).
        */}
        <div className="mx-auto max-w-[1440px] px-4 pb-16 pt-8 md:px-8">
          <EncabezadoDeMiEmpresa
            activa="documentos"
            titulo="Documentos"
            subtitulo="Cómo se numeran y qué dicen las cotizaciones que tu empresa envía a sus clientes."
            acciones={
              <>
                <Button variant="outline" onClick={() => navigate('/pricer-datec/quotes')}>
                  <ArrowLeft size={14} aria-hidden="true" /> Volver al listado
                </Button>
                {puedeEditar && (
                  <Button
                    variant="degradado"
                    className="px-5"
                    onClick={guardar}
                    disabled={!hayCambios || hayError || guardando}
                  >
                    <Save size={14} aria-hidden="true" /> {guardando ? 'Guardando…' : 'Guardar'}
                  </Button>
                )}
              </>
            }
          />
          <div style={{ maxWidth: 1080, marginTop: 24 }}>
            {cargando && (
              <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 13 }}>Cargando…</div>
            )}

            {errorDeCarga && (
              <div
                role="alert"
                style={{
                  ...caja,
                  borderColor: 'hsl(var(--bloqueo-borde))',
                  color: 'hsl(var(--bloqueo))',
                }}
              >
                {errorDeCarga}{' '}
                <button type="button" style={enlaceBoton} onClick={() => void cargar()}>
                  Reintentar
                </button>
              </div>
            )}

            {datos && cfg && cite && (
              <>
                {bloqueado && (
                  <div
                    data-testid="aviso-solo-lectura"
                    style={{
                      ...caja,
                      background: 'hsl(var(--muted))',
                      fontSize: 13,
                      color: 'hsl(var(--foreground) / 0.8)',
                    }}
                  >
                    Sólo el administrador de la empresa puede cambiar esta configuración. Acá la
                    podés ver.
                  </div>
                )}

                <section style={caja} data-testid="cfg-cite">
                  <h3 style={titulo}>Numeración de cotizaciones (CITE)</h3>

                  <div
                    data-testid="cite-proximo-vista"
                    style={{
                      padding: '10px 14px',
                      background: 'hsl(var(--info-suave))',
                      border: '1px solid hsl(var(--info-borde))',
                      borderRadius: 6,
                      marginBottom: 14,
                    }}
                  >
                    <div style={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.8)' }}>
                      El próximo número será
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'hsl(var(--info))' }}>
                      <span className="pd-mono" data-testid="cite-ejemplo">
                        {proximo?.ejemplo}
                      </span>
                    </div>
                    <div
                      style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginTop: 4 }}
                    >
                      Es de sólo lectura: se calcula con el último número usado y el formato de
                      abajo.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                    <div>
                      <label className="pd-field-label" htmlFor="cite-prefijo">
                        Prefijo
                      </label>
                      <input
                        id="cite-prefijo"
                        className="pd-field-input"
                        value={cite.prefijo}
                        maxLength={20}
                        disabled={bloqueado}
                        onChange={(e) => cambiarCite({ prefijo: e.target.value.toUpperCase() })}
                      />
                      {prefijoMalo && (
                        <div role="alert" style={{ ...ayuda, color: 'hsl(var(--bloqueo))' }}>
                          El prefijo sólo admite letras, números, espacio y . _ / - (hasta 20).
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="pd-field-label" htmlFor="cite-sep">
                        Separador
                      </label>
                      <select
                        id="cite-sep"
                        className="pd-field-input"
                        value={cite.separador}
                        disabled={bloqueado}
                        onChange={(e) =>
                          cambiarCite({ separador: e.target.value as SeparadorCite })
                        }
                      >
                        <option value="-">Guion ( - )</option>
                        <option value="/">Barra ( / )</option>
                        <option value=".">Punto ( . )</option>
                      </select>
                    </div>
                    <div>
                      <label className="pd-field-label" htmlFor="cite-anio">
                        Año
                      </label>
                      <select
                        id="cite-anio"
                        className="pd-field-input"
                        value={cite.anio}
                        disabled={bloqueado}
                        onChange={(e) => cambiarCite({ anio: e.target.value as AnioCite })}
                      >
                        <option value="AAAA">AAAA (2026)</option>
                        <option value="AA">AA (26)</option>
                        <option value="NO">No incluir</option>
                      </select>
                    </div>
                    <div>
                      <label className="pd-field-label" htmlFor="cite-digitos">
                        Dígitos del correlativo
                      </label>
                      <select
                        id="cite-digitos"
                        className="pd-field-input"
                        value={cite.digitos}
                        disabled={bloqueado}
                        onChange={(e) => cambiarCite({ digitos: Number(e.target.value) })}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <option key={n} value={n}>
                            {n} ({'0'.repeat(n - 1)}1)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                      <span className="pd-field-label">Se incluye en el número</span>
                      <Casilla
                        texto="Mes (MM)"
                        valor={cite.mes}
                        deshabilitado={bloqueado}
                        onChange={(v) => cambiarCite({ mes: v })}
                      />
                      <Casilla
                        texto="Sigla del LOB principal de la cotización"
                        valor={cite.lob}
                        deshabilitado={bloqueado}
                        onChange={(v) => cambiarCite({ lob: v })}
                      />
                      <Casilla
                        texto="Iniciales del ejecutivo"
                        valor={cite.ejecutivo}
                        deshabilitado={bloqueado}
                        onChange={(v) => cambiarCite({ ejecutivo: v })}
                      />
                    </div>
                    <div>
                      <span className="pd-field-label">Reinicio del correlativo</span>
                      <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                        <Radio<ReinicioCite>
                          nombre="reinicio"
                          valor="ANUAL"
                          actual={cite.reinicio}
                          deshabilitado={bloqueado}
                          onChange={(v) => cambiarCite({ reinicio: v })}
                        >
                          Cada año (vuelve a 1 el 1 de enero)
                        </Radio>
                        <Radio<ReinicioCite>
                          nombre="reinicio"
                          valor="NUNCA"
                          actual={cite.reinicio}
                          deshabilitado={bloqueado}
                          onChange={(v) => cambiarCite({ reinicio: v })}
                        >
                          Nunca
                        </Radio>
                      </div>
                    </div>
                  </div>

                  {problemaRepeticion && (
                    <div
                      role="alert"
                      data-testid="aviso-repeticion"
                      style={{ ...ayuda, color: 'hsl(var(--alerta))', fontSize: 12, marginTop: 10 }}
                    >
                      {problemaRepeticion}
                    </div>
                  )}
                  <div style={ayuda}>
                    El «LOB principal» es el LOB con mayor total en la cotización. Si no hay ítems
                    con LOB sale «XXX».
                  </div>

                  <div style={{ marginTop: 16, maxWidth: 340 }}>
                    <label className="pd-field-label" htmlFor="cite-primero">
                      Primer número
                    </label>
                    <input
                      id="cite-primero"
                      className="pd-field-input"
                      inputMode="numeric"
                      value={String(cite.primerNumero)}
                      disabled={bloqueado}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/\D/g, ''));
                        cambiarCite({ primerNumero: Math.min(Math.max(n, 1), 99_999_999) });
                      }}
                    />
                    <div style={ayuda}>
                      Desde dónde arranca la numeración. Si ya se usaron números más altos, no los
                      baja.
                    </div>
                  </div>

                  <div
                    data-testid="aviso-formato"
                    style={{
                      marginTop: 16,
                      padding: '8px 12px',
                      background: 'hsl(var(--muted) / 0.5)',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                      fontSize: 12,
                      color: 'hsl(var(--foreground) / 0.8)',
                    }}
                  >
                    Cambiar el formato <strong>no cambia los números ya asignados</strong>. El
                    número se asigna cuando la cotización se envía a aprobación; mientras tanto
                    lleva su código interno y el documento dice «Borrador — sin número».
                  </div>
                </section>

                <section style={caja}>
                  <h3 style={titulo}>Condiciones por defecto</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="pd-field-label" htmlFor="cfg-pago">
                        Forma de pago
                      </label>
                      <input
                        id="cfg-pago"
                        className="pd-field-input"
                        value={cfg.textos.formaPago}
                        maxLength={200}
                        disabled={bloqueado}
                        onChange={(e) => cambiarTextos({ formaPago: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="pd-field-label" htmlFor="cfg-plazo">
                        Plazo de entrega
                      </label>
                      <input
                        id="cfg-plazo"
                        className="pd-field-input"
                        value={cfg.textos.plazoEntrega}
                        maxLength={200}
                        disabled={bloqueado}
                        onChange={(e) => cambiarTextos({ plazoEntrega: e.target.value })}
                      />
                    </div>
                  </div>
                  {textosVacios && (
                    <div role="alert" style={{ ...ayuda, color: 'hsl(var(--bloqueo))' }}>
                      La forma de pago y el plazo de entrega no pueden quedar vacíos.
                    </div>
                  )}
                  <div style={{ marginTop: 14 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <label className="pd-field-label" htmlFor="cfg-notas">
                        Notas
                      </label>
                      {!bloqueado && (
                        <button
                          type="button"
                          style={enlaceBoton}
                          onClick={() => cambiarTextos({ notas: datos.porDefecto.textos.notas })}
                        >
                          Volver al texto original
                        </button>
                      )}
                    </div>
                    <textarea
                      id="cfg-notas"
                      className="pd-field-input"
                      rows={4}
                      value={cfg.textos.notas}
                      maxLength={4000}
                      disabled={bloqueado}
                      onChange={(e) => cambiarTextos({ notas: e.target.value })}
                    />
                    <div
                      style={{
                        ...ayuda,
                        textAlign: 'right',
                        color:
                          cfg.textos.notas.length > LIMITE_SUAVE_TEXTO
                            ? 'hsl(var(--alerta))'
                            : 'hsl(var(--muted-foreground))',
                      }}
                    >
                      {cfg.textos.notas.length} / {LIMITE_SUAVE_TEXTO}
                    </div>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <label className="pd-field-label" htmlFor="cfg-terminos">
                        Disclaimer (términos y condiciones)
                      </label>
                      {!bloqueado && (
                        <button
                          type="button"
                          aria-label="Volver al texto original del Disclaimer (términos y condiciones)"
                          style={enlaceBoton}
                          onClick={() =>
                            cambiarTextos({ disclaimer: datos.porDefecto.textos.disclaimer })
                          }
                        >
                          Volver al texto original
                        </button>
                      )}
                    </div>
                    <textarea
                      id="cfg-terminos"
                      className="pd-field-input"
                      rows={5}
                      value={cfg.textos.disclaimer}
                      maxLength={4000}
                      disabled={bloqueado}
                      onChange={(e) => cambiarTextos({ disclaimer: e.target.value })}
                    />
                    <div
                      style={{
                        ...ayuda,
                        textAlign: 'right',
                        color:
                          cfg.textos.disclaimer.length > LIMITE_SUAVE_TEXTO
                            ? 'hsl(var(--alerta))'
                            : 'hsl(var(--muted-foreground))',
                      }}
                    >
                      {cfg.textos.disclaimer.length} / {LIMITE_SUAVE_TEXTO}
                    </div>
                  </div>
                  <div style={ayuda}>
                    Son los textos con los que arranca cada cotización nueva; cada cotización puede
                    cambiarlos. Las cotizaciones ya enviadas conservan los suyos.
                  </div>
                </section>

                {doc && (
                  <>
                    <section style={caja} data-testid="cfg-pie">
                      <h3 style={titulo}>Datos de la oficina (pie de cada hoja)</h3>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Campo
                          etiqueta="Oficina"
                          valor={doc.pie.oficina}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ pie: { ...doc.pie, oficina: v } })}
                        />
                        <Campo
                          etiqueta="Dirección de la oficina"
                          valor={doc.pie.direccion}
                          max={160}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ pie: { ...doc.pie, direccion: v } })}
                        />
                        <Campo
                          etiqueta="Ciudad y país"
                          valor={doc.pie.ciudad}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ pie: { ...doc.pie, ciudad: v } })}
                        />
                        <Campo
                          etiqueta="Teléfono central"
                          tipo="tel"
                          valor={doc.pie.telefono}
                          max={40}
                          disabled={bloqueado}
                          problema={telefonoMalo(doc.pie.telefono) ? MENSAJE_TELEFONO : null}
                          onChange={(v) => cambiarDoc({ pie: { ...doc.pie, telefono: v } })}
                        />
                        <Campo
                          etiqueta="Sitio web"
                          valor={doc.pie.web}
                          max={120}
                          disabled={bloqueado}
                          placeholder="www.empresa.com"
                          ayudaCorta="Con o sin http://: se guarda sin protocolo."
                          problema={webConProtocolo ? 'Escribí la web sin http://' : null}
                          onChange={(v) => cambiarDoc({ pie: { ...doc.pie, web: limpiarWeb(v) } })}
                        />
                      </div>
                      <div style={ayuda}>
                        Se escriben una sola vez y salen al pie de cada hoja del documento.
                      </div>
                    </section>

                    <section style={caja} data-testid="cfg-firma">
                      <h3 style={titulo}>Firma de ejemplo</h3>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Campo
                          etiqueta="Saludo"
                          valor={doc.firma.saludo}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ firma: { ...doc.firma, saludo: v } })}
                        />
                        <Campo
                          etiqueta="Equipo preventa"
                          valor={doc.firma.equipo}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ firma: { ...doc.firma, equipo: v } })}
                        />
                        <Campo
                          etiqueta="Razón social"
                          valor={doc.firma.razonSocial}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ firma: { ...doc.firma, razonSocial: v } })}
                        />
                        <Campo
                          etiqueta="Lema"
                          valor={doc.firma.lema}
                          max={80}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ firma: { ...doc.firma, lema: v } })}
                        />
                      </div>
                      <div style={ayuda}>
                        Cada cotización parte de este ejemplo y lo ajusta sola: el correo del
                        comercial se escribe en la cotización.
                      </div>
                    </section>

                    <section style={caja} data-testid="cfg-textos-nuevos">
                      <h3 style={titulo}>Condiciones por defecto del documento</h3>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Campo
                          etiqueta="Condición comercial"
                          valor={doc.textos.condicionComercial}
                          max={200}
                          disabled={bloqueado}
                          onChange={(v) =>
                            cambiarDoc({ textos: { ...doc.textos, condicionComercial: v } })
                          }
                        />
                        <Campo
                          etiqueta="Impuestos"
                          valor={doc.textos.impuestos}
                          max={200}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ textos: { ...doc.textos, impuestos: v } })}
                        />
                        <Campo
                          etiqueta="Cobertura y garantía"
                          valor={doc.textos.cobertura}
                          max={200}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ textos: { ...doc.textos, cobertura: v } })}
                        />
                        <Campo
                          etiqueta="Otras condiciones"
                          valor={doc.textos.otros}
                          max={500}
                          disabled={bloqueado}
                          onChange={(v) => cambiarDoc({ textos: { ...doc.textos, otros: v } })}
                        />
                      </div>
                      <div style={ayuda}>
                        Son los textos con los que arranca cada cotización nueva; cada cotización
                        puede cambiarlos.
                      </div>
                    </section>

                    <section style={caja} data-testid="cfg-biblioteca">
                      <h3 style={titulo}>Biblioteca de bloques del anexo técnico</h3>
                      <div style={ayuda}>
                        Bloques reutilizables (por ejemplo «Servicios profesionales»). En cada
                        cotización se agregan, se ordenan y se editan sin tocar esta biblioteca.
                      </div>
                      <AnexoTecnicoEditor
                        modoBiblioteca
                        anexo={{ titulo: '', subtitulo: '', bloques: doc.biblioteca }}
                        soloLectura={bloqueado}
                        onChange={(a) => cambiarDoc({ biblioteca: a.bloques })}
                      />
                    </section>

                    <section style={caja} data-testid="cfg-anexo-ejemplo">
                      <h3 style={titulo}>Anexo técnico de ejemplo</h3>
                      <div style={ayuda}>
                        Es el anexo con el que arranca una cotización cuando se prende «Anexo
                        técnico».
                      </div>
                      <AnexoTecnicoEditor
                        anexo={doc.anexoEjemplo}
                        biblioteca={doc.biblioteca}
                        soloLectura={bloqueado}
                        onChange={(anexoEjemplo) => cambiarDoc({ anexoEjemplo })}
                      />
                    </section>

                    <div
                      data-testid="aviso-no-cambia-enviadas"
                      style={{
                        marginBottom: 18,
                        padding: '8px 12px',
                        background: 'hsl(var(--muted) / 0.5)',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 6,
                        fontSize: 12,
                        color: 'hsl(var(--foreground) / 0.8)',
                      }}
                    >
                      Cambiar esto <strong>no cambia las cotizaciones ya enviadas</strong>:
                      conservan la firma, el pie y el anexo con los que se enviaron.
                    </div>
                  </>
                )}

                {puedeEditar && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={guardar} disabled={!hayCambios || hayError || guardando}>
                      <Save size={14} className="mr-1.5" /> {guardando ? 'Guardando…' : 'Guardar'}
                    </Button>
                    <Button variant="outline" onClick={restaurar} disabled={guardando}>
                      Restaurar valores de fábrica
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
